import { useQueryClient } from '@tanstack/react-query'
import { Form, Formik } from 'formik'
import React, { useState } from 'react'
import * as Yup from 'yup'
import { useChangePasswordRegistration } from '../../api/account/apiRegistration'
import { ApiErrorBody } from '../../api/apiUtils'
import { ApiError, ChangePasswordDto } from '../../generated'
import { Button } from '../../genericComponents/Button'
import { Card } from '../../genericComponents/Card'
import { Note } from '../../genericComponents/Note'
import { Input } from '../../inputs/Input'
import { FTUWizardHeader } from '../components/FTUWizardHeader'
import { PasswordErrors } from '../resetPassword/ResetPasswordCard'
import { WizardWrapper } from '../WizardWrapper'

export const FTUChangePasswordCard: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [errorMessagesCurrentPassword, setErrorMessagesCurrentPassword] = useState<string[]>([])
  const queryClient = useQueryClient()

  const { mutate: changePassword } = useChangePasswordRegistration()
  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required('Current Password is required'),
    newPassword: Yup.string()
      .required('New Password is required')
      .min(8, PasswordErrors.MIN_LENGTH)
      .matches(/\d+/gi, PasswordErrors.DIGIT_CHAR)
      .matches(/[!@#$%^&*()_+]/gi, PasswordErrors.SPECIAL_CHAR)
      .matches(/[A-Z]+/g, PasswordErrors.UPPER_CASE_CHAR),
    newPasswordAgain: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Repeat New Password is required'),
  })

  const handleChangePassword = (
    values: { currentPassword: string; newPassword: string; newPasswordAgain: string },
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    setErrorMessagesCurrentPassword([])
    setErrorMessages([])
    setErrorMessage(null)

    changePassword(
      {
        newPassword: values.newPassword,
        oldPassword: values.currentPassword,
      },
      {
        onSuccess: () => {
          setSubmitting(false)
          void queryClient.invalidateQueries({ queryKey: ['registrationState'] })
        },

        onError: ((error: ApiError) => {
          const subErrors = (error.body as ApiErrorBody).apierror.subErrors?.[0]
          const softlockErrors = subErrors?.softlockErrors

          if (softlockErrors.length > 0) {
            const errorsWithoutCurrentPasswordError = softlockErrors
              .filter((v) => v != 'PASSWORD_DOES_NOT_MATCH')
              .map((v) => PasswordErrors[v])
            const errorsWithCurrentPasswordError = softlockErrors
              .filter((v) => v === 'PASSWORD_DOES_NOT_MATCH')
              .map((v) => PasswordErrors[v])

            if (errorsWithoutCurrentPasswordError.length > 0) {
              setErrorMessages(errorsWithoutCurrentPasswordError)
            }

            if (errorsWithCurrentPasswordError.length > 0) {
              setErrorMessagesCurrentPassword(errorsWithCurrentPasswordError)
            }
          } else {
            setErrorMessage((error.body as ApiErrorBody).apierror.message)
          }
          setSubmitting(false)
        }) as (error: unknown, variables: ChangePasswordDto, context: unknown) => void,
      }
    )
  }

  return (
    <Card title="">
      <WizardWrapper>
        <h1 className="text-primary font-bold text-2xl uppercase text-center tracking-widest m-3 my-6">
          Change Password
        </h1>
        <div>
          <FTUWizardHeader wizardStepCount={4} wizardActiveStep={1} />
        </div>

        <Formik
          initialValues={{ currentPassword: '', newPassword: '', newPasswordAgain: '' }}
          validationSchema={validationSchema}
          onSubmit={(values, { setSubmitting }) => handleChangePassword(values, setSubmitting)}
        >
          {({ isSubmitting }) => (
            <Form>
              <p className="my-6">
                You signed in with an expired password. Please change your password to continue.
              </p>
              <Input name="currentPassword" type="password" label="Enter the current password" />
              {errorMessagesCurrentPassword &&
                errorMessagesCurrentPassword.map((errorMessage) => (
                  <div className="flex">
                    <span className="text-alertColor text-sm font-semibold italic">
                      {errorMessage}
                    </span>
                  </div>
                ))}
              <Note className="mt-4">
                The password must contain at least 8 alphanumeric characters, at least one number
                and one uppercase letter and have at least one special character.
              </Note>
              <Input name="newPassword" type="password" label="New Password" />

              {errorMessages &&
                errorMessages.map((errorMessage) => (
                  <div className="flex">
                    <span className="text-alertColor text-sm font-semibold italic">
                      {errorMessage}
                    </span>
                  </div>
                ))}

              <Input name="newPasswordAgain" type="password" label="Repeat New Password" />
              {errorMessage && (
                <div className="flex">
                  <span className="text-alertColor text-sm font-semibold italic">
                    {errorMessage}
                  </span>
                </div>
              )}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary text-white mt-4 uppercase"
              >
                CHANGE PASSWORD
              </Button>
            </Form>
          )}
        </Formik>
      </WizardWrapper>
    </Card>
  )
}
